/**
 * Leads Page
 * Funil de leads: quadro por estágio (arrastar) ou lista; a ficha abre por cima.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { LayoutGrid, List, Plus, Search, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { leadsService } from '../services/leads-service';
import type { Lead, LeadFilters } from '../types/leads';
import { LeadStage } from '../types/leads';
import { LeadsKanbanView } from '../components/leads/LeadsKanbanView';
import { LeadsListView } from '../components/leads/LeadsListView';
import { LeadDetailsModal } from '../components/leads/LeadDetailsModal';
import { CreateLeadModal } from '../components/leads/CreateLeadModal';

type LeadsView = 'kanban' | 'list';

export const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<LeadsView>('kanban');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState<LeadFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await leadsService.list(filters, { limit: 100, sortBy: 'createdAt', sortOrder: 'DESC' });
      setLeads(response.leads);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleCreateLead = async () => {
    await loadLeads();
    setShowCreateModal(false);
    toast.success('Lead criado com sucesso!');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchTerm });
  };

  const handleStageChange = async (leadId: string, newStage: LeadStage) => {
    try {
      await leadsService.updateStage(leadId, { stage: newStage });
      await loadLeads();
      toast.success('Estágio atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar estágio:', error);
      toast.error('Erro ao atualizar estágio');
    }
  };

  // A ficha acompanha a lista recarregada (estágio novo, conversão…).
  const selectedLead = leads.find((lead) => lead.id === selectedLeadId) ?? null;
  const openLead = (lead: Lead) => setSelectedLeadId(lead.id);

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Target className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Leads</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie seus leads e oportunidades</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Novo Lead
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={view} onValueChange={(next) => setView(next as LeadsView)}>
          <TabsList>
            <TabsTrigger value="kanban"><LayoutGrid className="size-4" /> Kanban</TabsTrigger>
            <TabsTrigger value="list"><List className="size-4" /> Lista</TabsTrigger>
          </TabsList>
        </Tabs>
        <form onSubmit={handleSearch} className="flex w-full items-center gap-2 sm:w-auto" role="search">
          <Input
            aria-label="Buscar leads"
            placeholder="Buscar leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sm:w-64"
          />
          <Button type="submit" variant="outline" size="icon" aria-label="Buscar">
            <Search className="size-4" />
          </Button>
        </form>
      </div>

      {loading && leads.length === 0 ? (
        <div className="flex gap-3 overflow-hidden" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-96 w-72 shrink-0" />)}
        </div>
      ) : view === 'kanban' ? (
        <LeadsKanbanView leads={leads} onStageChange={handleStageChange} onLeadClick={openLead} />
      ) : (
        <Card>
          <CardContent>
            <LeadsListView leads={leads} onLeadClick={openLead} />
          </CardContent>
        </Card>
      )}

      {selectedLead && (
        <LeadDetailsModal lead={selectedLead} onClose={() => setSelectedLeadId(null)} onUpdate={loadLeads} />
      )}
      {showCreateModal && <CreateLeadModal onClose={() => setShowCreateModal(false)} onSuccess={handleCreateLead} />}
    </div>
  );
};

export default LeadsPage;
